import React from 'react';
import keyBy from 'lodash/keyBy';
import isNil from 'lodash/isNil';
import {
  useFormContext,
} from 'react-hook-form';
import {
  ClaimBenefitsAssignmentSettingDTO,
  ClaimInsuranceTypeSettingDTO,
  ClaimPlanParticipationSettingsDTO,
  InsuranceDTO,
} from 'dtos';
import Controller from 'core/components/FormController';
import Accordion from 'core/components/Accordion';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Select from 'core/components/Select';
import {
  useClaimSettings,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  ClaimDataForm,
  NotSetSentinal,
} from 'pages/Dashboard/pages/Billing/types';
import PatientInsuranceSelect from 'pages/Dashboard/components/PatientInsuranceSelect';
import {
  claimEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type Props = {
  patientId?: number;
};

export default function ClaimPayer({ patientId }: Props) {
  const { control, getValues, setValue } = useFormContext<ClaimDataForm>();

  const {
    benefitsAssignmentSettings,
    insuranceTypeSettings,
    insurancePolicySettings,
    planParticipationSettings,
  } = useClaimSettings();

  const claimBenefitsAssignmentSettingsWithNotSet = [{ code: NotSetSentinal },
    ...(benefitsAssignmentSettings ?? [])];

  const claimInsuranceTypeSettingsWithNotSet = [{ code: NotSetSentinal },
    ...(insuranceTypeSettings ?? [])];

  const claimInsurancePolicySettingsWithNotSet = [{ code: NotSetSentinal },
    ...(insurancePolicySettings ?? [])];

  const claimPlanParticipationSettingsWithNotSet = [{ code: NotSetSentinal },
    ...(planParticipationSettings ?? [])];

  const keyedInsurancePolicyOptionsWithNotSet = keyBy(claimInsurancePolicySettingsWithNotSet, 'code');

  function resetAuthorization() {
    setValue('patientAuthorizationSelection', {
      authorizationNumber: null,
    }, { shouldDirty: true });

    const claimId = getValues('claimId');
    const billingCodes = getValues('billingCodes');
    if (billingCodes) {
      setValue('billingCodes', billingCodes.map((code) => ({
        ...code,
        serviceCodeDetails: code.serviceCodeDetails
          && (code.serviceCodeDetails.primaryClaimId === claimId
            || code.serviceCodeDetails.secondaryClaimId === claimId)
          ? {
            ...code.serviceCodeDetails,
            priorAuthCode: null,
          }
          : code.serviceCodeDetails,
        claimServiceLine: code.claimServiceLine
          && (code.serviceCodeDetails?.primaryClaimId === claimId
            || code.serviceCodeDetails?.secondaryClaimId === claimId)
          ? { ...code.claimServiceLine, authorizationNumber: null }
          : code.claimServiceLine,
      })), { shouldDirty: true });
    }
  }

  return (
    <Accordion
      defaultExpanded
      header="Payer"
      disableGutters
      headerClasses={{
        root: '!px-2 !py-1 !min-h-[initial] !bg-gray-100 !text-gray-600 !font-semibold text-sm',
        content: '!m-0',
      }}
      detailsClasses={{ root: '!p-2' }}
    >
      <div className="flex flex-col gap-2 populate-form">
        <div className="flex items-center w-full gap-3">
          <Controller
            control={control}
            name="insurance"
            render={({ field: { value, ...field } }) => (
              <Wrapper
                id="patient-insurance"
                label="Insurance"
                className="w-1/3 !min-w-[400px]"
              >
                <PatientInsuranceSelect
                  onChange={(insurance: InsuranceDTO | InsuranceDTO[]) => {
                    const selectedInsurance = insurance as InsuranceDTO;
                    if ((value?.insuranceId ?? 0) !== (selectedInsurance?.insuranceId ?? 0)) {
                      resetAuthorization();
                    }

                    field.onChange(selectedInsurance);
                    claimEventChannel.emit('onShouldUpdateBillingProvider');
                    claimEventChannel.emit('onShouldUpdateInsuranceType');
                  }}
                  className="w-full"
                  insuranceId={value?.insuranceId ?? 0}
                  patientId={patientId ?? 0}
                  detailedView
                />
              </Wrapper>
            )}
          />
        </div>
        <div className="flex items-center w-full gap-3">
          <div className="w-1/6">
            <Controller
              control={control}
              name="insurancePolicy"
              render={({ field }) => (
                <Wrapper
                  id="insurance-policy"
                  label="Insurance Policy"
                  className="flex-1"
                >
                  <div className="p-2 border border-gray-200 rounded text-xs w-full bg-gray-100">
                    {field.value && field.value.code === NotSetSentinal
                      ? 'Not Set'
                      : `${field.value ? field.value.code : ''} - ${
                        keyedInsurancePolicyOptionsWithNotSet[!isNil(field.value) ? field.value.code ?? '' : '']?.insurancePolicy ?? '--'
                      }`}
                  </div>
                </Wrapper>
              )}
            />
          </div>
          <div className="w-1/3">
            <Controller
              control={control}
              name="insuranceType"
              render={({ field }) => (
                <Select<ClaimInsuranceTypeSettingDTO>
                  {...field}
                  id="insurance-type"
                  className="flex-1 text-gray-500 text-xs"
                  label="Insurance Type (Box 1)"
                  options={claimInsuranceTypeSettingsWithNotSet ?? []}
                  getLabel={(option) =>
                    (option?.code === NotSetSentinal
                      ? 'Not Set'
                      : `${option.code} - ${option.insuranceType}`)}
                  getValue={(option) => option?.code ?? ''}
                />
              )}
            />
          </div>
        </div>
        <div className="flex items-center w-full gap-3">
          <div className="w-1/3">
            <Controller
              control={control}
              name="planParticipation"
              render={({ field }) => (
                <Select<ClaimPlanParticipationSettingsDTO>
                  {...field}
                  id="plan-participation"
                  className="flex-1 text-gray-500 text-xs"
                  label="Accept Assignment (Box 27)"
                  placeholder="Select"
                  options={claimPlanParticipationSettingsWithNotSet ?? []}
                  getLabel={(option) =>
                    (option?.code === NotSetSentinal
                      ? 'Not Set'
                      : `${option.code} - ${option?.description}`)}
                  getValue={(option) => option?.code ?? ''}
                />
              )}
            />
          </div>
          <div className="w-1/6">
            <Controller
              control={control}
              name="benefitsAssignment"
              render={({ field }) => (
                <Select<ClaimBenefitsAssignmentSettingDTO>
                  {...field}
                  id="plan-participation"
                  className="flex-1 text-gray-500 text-xs"
                  label="Authorize Benefits (Box 13)"
                  placeholder="Select"
                  options={claimBenefitsAssignmentSettingsWithNotSet ?? []}
                  getLabel={(option) =>
                    (option?.code === NotSetSentinal
                      ? 'Not Set'
                      : `${option.code} - ${option?.description}`)}
                  getValue={(option) => option?.code ?? ''}
                />
              )}
            />
          </div>
        </div>
      </div>
    </Accordion>
  );
}
